/**
 * 申报助手协议纯函数（与 minmax-agent 契约的唯一事实源；无框架依赖，node:test 直接断言）。
 * 同步义务：本文件的映射/清单变更必须与 agent 侧代码同步（各常量已标注来源行）。
 */

// interrupt_payload.type → 卡片组件名（agent collection.py / planning.py 的 interrupt 类型）
export const INTERRUPT_CARD_MAP = {
  collect_fields: 'CollectFieldsCard',
  collect_dialogue: 'DialogueCard',
  collection_confirm: 'ConfirmProfileCard',
  plan_review: 'PlanReviewCard',
  intent_confirm: 'IntentConfirmCard',
  intent_select: 'IntentSelectCard',
  intent_dialogue: 'DialogueCard',
  extract_confirm: 'ExtractConfirmCard',
  company_bind: 'CompanyBindCard'
}

// phase → 文案（agent graph 节点输出 phase；同步义务：agent phase 变更时更新）
// 覆盖集合见 app/agent 各节点 "phase" 输出（intent/policy_acquisition/extract/collection/evaluation/planning/graph）
export const PHASE_LABELS = {
  created: '已创建',
  intent_ready: '意向已识别',
  intent_need_select: '待选择政策',
  intent_confirmed: '意向已确认',
  consulted: '已转咨询',
  policy_acquired: '政策已获取',
  extract_done: '附件抽取完成',
  template_derived: '模板已推导',
  profile_confirmed: '画像已确认',
  evaluated: '判定完成',
  gaps_analyzed: '差距分析完成',
  products_matched: '产品匹配完成',
  plan_confirmed: '方案已确认',
  done: '已完成',
  SUSPENDED: '已挂起',
  resumed: '已恢复'
}

// 与 agent planning.py:35 EDITABLE_PLAN_FIELDS 同步（变更需双侧同步）
export const EDITABLE_PLAN_FIELDS = ['timeline', 'quotation', 'quotation_total', 'product_matches']

const PLAN_REVIEW_ACTIONS = new Set(['approve', 'suspend', 'edit'])

/**
 * 按 seq 幂等归并事件（SSE 每次连接从 seq=0 全量重放，seq 为唯一键）。
 * @param {Array} existing 已归并事件
 * @param {Array} incoming 新到事件
 * @returns {Array} 按 seq 升序的去重全集
 */
export function mergeEvents(existing, incoming) {
  const bySeq = new Map()
  for (const e of existing) if (e && e.seq != null) bySeq.set(e.seq, e)
  for (const e of incoming) if (e && e.seq != null) bySeq.set(e.seq, e)
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq)
}

/**
 * plan_review resume 构造器（禁令②：必显式带 action，杜绝空 payload 默认 approve）。
 */
export function buildPlanReviewResume(action, edits) {
  if (!PLAN_REVIEW_ACTIONS.has(action)) {
    throw new Error(`非法审核动作: ${action}`)
  }
  const payload = { action }
  if (action === 'edit') payload.edits = edits || {}
  return { payload }
}

/** 挂起/中断恢复。 */
export function buildContinueResume() {
  return { payload: { action: 'continue' } }
}

/**
 * resume 用户应答 → 留痕气泡文本（对话原文/选择内容明细；空串 = 不留痕）。
 * 与 Java AgentConsoleAppService.describeUserAction 同步（变更需双侧同步）。
 * @param {object} payload
 * @returns {string}
 */
export function describeUserPayload(payload) {
  if (!payload) return ''
  const text = String(payload.text || '').trim()
  if (text) return text
  let summary = ''
  const action = payload.action
  if (action == null) {
    summary = payload.values ? '提交了申报信息' : ''
  } else {
    switch (String(action)) {
      case 'approve': summary = '已确认/批准'; break
      case 'suspend': summary = '已挂起任务'; break
      case 'bind': summary = `绑定企业：${payload.company_id}`; break
      case 'select':
      case 'confirm': summary = `选择政策：${payload.policy_code}`; break
      case 'edit': summary = '已编辑提交'; break
      case 'reject': summary = '已弃用抽取结果'; break
      case 'continue': summary = '继续执行'; break
      case 'consult': summary = '转为咨询'; break
      case 'skip': summary = '跳过推荐，手动选择政策'; break
      default: summary = ''
    }
  }
  if (!summary) return ''
  const detail = describePayloadDetail(payload)
  return detail ? `${summary}：${detail}` : summary
}

/** 提交内容明细：values/edits → "键=值、…"；skip → "跳过：…"（上限 500 字，与 Java 侧一致）。 */
function describePayloadDetail(payload) {
  const pairs = []
  const values = payload.values || payload.edits
  if (values && typeof values === 'object') {
    for (const [k, v] of Object.entries(values)) {
      pairs.push(`${k}=${v == null ? '' : v}`)
    }
  }
  let detail = pairs.join('、')
  if (Array.isArray(payload.skip) && payload.skip.length) {
    detail += `${detail ? '；' : ''}跳过：${payload.skip.join('、')}`
  }
  return detail.length > 500 ? detail.slice(0, 500) + '…' : detail
}

/**
 * 任务快照 → agent 应答留痕气泡文本（interrupt 问题/摘要，或终态；空串 = 不产生气泡）。
 * 与 Java AgentConsoleAppService.describeAgentReply 同步（变更需双侧同步）。
 * @param {object} snap 任务快照（TaskResponse）
 * @returns {string}
 */
export function describeAgentReply(snap) {
  if (!snap) return ''
  const payload = snap.interrupt_payload
  if (payload && typeof payload === 'object') {
    switch (String(payload.type)) {
      case 'collect_dialogue':
      case 'intent_dialogue':
        return String(payload.question || '')
      case 'collect_fields':
        return `请补充 ${Array.isArray(payload.missing) ? payload.missing.length : 0} 项申报信息`
      case 'collection_confirm': return '请确认企业资质画像'
      case 'plan_review': return '申报方案已生成，请审核'
      case 'intent_confirm': return '为您推荐以下政策候选，请确认'
      case 'intent_select': return '请选择要申报的政策'
      case 'extract_confirm':
        return `已解析 ${Array.isArray(payload.extracts) ? payload.extracts.length : 0} 份资料，请确认抽取结果`
      case 'company_bind': return '定稿前请先绑定企业'
      case 'suspended': return `任务已挂起：${payload.reason}`
      default: return ''
    }
  }
  switch (String(snap.status || '')) {
    case 'done': {
      const planNo = snap.plan_json && snap.plan_json.plan_no
      return planNo ? `方案已定稿：${planNo}` : '咨询已完成'
    }
    case 'failed': return `任务失败：${snap.error}`
    case 'suspended': return `任务已挂起：${snap.suspended_reason}`
    default: return ''
  }
}

/**
 * SSE 事件 → 即时气泡文本（事件到达即追加，不等操作完成；与 describeAgentReply 同口径）。
 * 仅 interrupt/suspended/failed/done 产气泡，phase/task_created 等其余事件返回空串。
 * @param {object} ev SSE 事件（data 内含 seq/type/payload）
 * @returns {string}
 */
export function describeAgentEvent(ev) {
  if (!ev || typeof ev !== 'object') return ''
  switch (ev.type) {
    case 'interrupt': return describeAgentReply({ interrupt_payload: ev.payload })
    case 'suspended': return `任务已挂起：${ev.reason}`
    case 'failed': return `任务失败：${ev.error}`
    case 'done': return ev.plan_no ? `方案已定稿：${ev.plan_no}` : '咨询已完成'
    default: return ''
  }
}

/** 快照 → 视图状态（六态；卡片类型由 status + interrupt_payload 决定）。 */
export function mapSnapshotToView(snap) {
  const status = snap.status
  let cardType = null
  if (status === 'awaiting' && snap.interrupt_payload) {
    cardType = INTERRUPT_CARD_MAP[snap.interrupt_payload.type] || null
  } else if (status === 'suspended') {
    cardType = 'SuspendedCard'
  } else if (status === 'interrupted') {
    cardType = 'InterruptedCard'
  } else if (status === 'done') {
    cardType = 'PlanDoneCard'
  } else if (status === 'failed') {
    cardType = 'FailedCard'
  }
  return { status, cardType }
}
